/**
 * Utility to determine which product fields should be sent to AI Review
 * based on the product's category. This ensures the AI only sees fields
 * that are actually visible/relevant in the UI for this product type.
 */

import IProduct, { NutritionalUnit } from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import {
  isNewFood,
  isNewNonFood,
  isFeedCategory,
  isFish,
  isEgg,
  isBeverages,
  isFruktOrGreen,
  isSunscreen,
  isMedical,
  isToy,
  isElectric,
  isGoods,
  isJewelry,
  isFoodSupplements,
  isSpecialFoodNutrition,
  isWhiteSnus,
  isPharma,
  isDetergent,
  isComplementarySupplements,
  isOtherFoodstuffs,
  isMeat,
  isBakeryProducts,
  isMilk,
  isCereals,
  isConfectionery,
  isConsumptionReadySnacks,
  isSweet,
  isFats,
  isSpicesSoupsSalads,
  isIceCream,
  isChemical,
  isCosmetic,
  isOTC,
  isCmrHazardCategory,
} from '../fields/category';
import { getPhraseSpec } from '../fields/hazard-placeholders';

/**
 * Fields that are ALWAYS sent regardless of category
 * These are basic identification fields shown for all products
 */
const ALWAYS_VISIBLE_FIELDS: (keyof IProduct | string)[] = [
  'foodlaCategory', // category - always needed for AI to determine product type
  'title',
  'EAN',
  'short_text',
  'descriptive_size_amount',
  'descriptive_size_unit',
  'trade_item_temperature_information',
  'labels',
  'basicDataTaxClassification',
  // Temperature fields (always shown)
  'min_temperature',
  'max_temperature',
  'search_terms',
];

/**
 * Fields visible ONLY for FOOD products
 */
const FOOD_ONLY_FIELDS: (keyof IProduct | string)[] = [
  'brand_food',
  'ingredient_statement',
  'nutritional_unit',
  'energi_kj',
  'energi_kcal',
  'fett',
  'mattatFett',
  'enkelomattatFett',
  'flerromattatFett',
  'kolhydrat',
  'sockerarter',
  'polyoler',
  'starkelse',
  'fiber',
  'protein',
  'salt',
  'place_of_item_activity',
  // Note: preparationInstructions is conditional - moved to CONDITIONAL_FIELDS
  // Vitamins/minerals
  'zink',
  'vitaminK',
  'vitaminE',
  'vitaminD',
  'vitaminC',
  'vitaminB6',
  'vitaminB12',
  'vitaminA',
  'tiamin',
  'selen',
  'riboflavin',
  'pantotensyra',
  'niacin',
  'molybden',
  'mangan',
  'magnesium',
  'krom',
  'koppar',
  'klorid',
  'kalium',
  'kalcium',
  'jod',
  'jarn',
  'fosfor',
  'folsyra',
  'fluorid',
  'biotin',
];

/**
 * Fields visible ONLY for NON-FOOD products (excluding feed)
 */
const NON_FOOD_ONLY_FIELDS: (keyof IProduct | string)[] = [
  'brand',
  // Note: non_food_ingredients is conditional - only for specific non-food subcategories
  'signalWords', // Always shown for non-food (excluding feed)
  // Note: hazardSymbols, hazardStatements, securityNotices are conditional based on signalWords
  // Note: additionalCharacteristics moved to CONDITIONAL_FIELDS (shown for ALL non-food including feed)
];

/**
 * Fields visible ONLY for DJURFODER (animal feed) products
 */
const FEED_ONLY_FIELDS: (keyof IProduct | string)[] = [
  'brand',
  'animalFeedType',
  'animalFoodIngredients',
  'animalCategoriesForFeed',
  'feedAdditives',
  'feedingInstructions',
  'analyticalConstituentsFeed',
  'place_of_item_activity',
  // Note: dosageAndUsageInstructions is NOT shown for feed (UI explicitly excludes it)
  'responsibleLabelingApprovalNumber',
  'animalAgeGroup',
];

/**
 * Conditional fields based on specific subcategories
 */
interface ConditionalField {
  fields: (keyof IProduct | string)[];
  condition: (product: Partial<IProduct>, rootCategory?: IFoodlaCategoryOption) => boolean;
}

export const shouldShowHazardDetails = (product: Partial<IProduct>, rootCategory?: IFoodlaCategoryOption) => {
  const showForPharmaOrCmr =
    (!!isPharma(product.foodlaCategory, rootCategory) || !!isCmrHazardCategory(product.foodlaCategory, rootCategory)) &&
    !(!!isChemical(product.foodlaCategory, rootCategory) || !!isToy(product.foodlaCategory, rootCategory));
  const signalWords = product.signalWords as unknown as string;
  const showForToyOrChemical =
    (!!isToy(product.foodlaCategory, rootCategory) || !!isChemical(product.foodlaCategory, rootCategory)) &&
    (signalWords === 'WARNING' || signalWords === 'DANGER');

  return showForPharmaOrCmr || showForToyOrChemical;
};

export const hasPhraseFillControls = (codes: unknown) =>
  Array.isArray(codes) && codes.some(code => typeof code === 'string' && !!getPhraseSpec(code));

export const shouldShowDailyDose = (product: Partial<IProduct>, isSupplement?: boolean) =>
  !!isSupplement && product.nutritional_unit === NutritionalUnit.DAGSDOS;

const CONDITIONAL_FIELDS: ConditionalField[] = [
  // Extra net quantities are rendered only after at least one has been added.
  {
    fields: ['descriptive_size_amount_extra'],
    condition: p => Array.isArray(p.descriptive_size_amount_extra) && p.descriptive_size_amount_extra.length > 0,
  },

  // Fish fields
  {
    fields: ['fishProductionMethod', 'fishCatchMethod', 'catchArea'],
    condition: (p, r) => !!isFish(p.foodlaCategory, r, p),
  },

  // Egg fields
  {
    fields: ['eggWeightClass', 'eggPackerApprovalNumber'],
    condition: (p, r) => !!isEgg(p.foodlaCategory, r),
  },

  // Beverage fields
  {
    fields: ['alcoholPercentage', 'recycling_amount', 'recyclingType'],
    condition: (p, r) => !!isBeverages(p.foodlaCategory, r),
  },

  // Fruit/vegetable fields
  { fields: ['classification'], condition: (p, r) => !!isFruktOrGreen(p.foodlaCategory, r) },

  // Sunscreen
  { fields: ['sunProtectionFactor'], condition: (p, r) => !!isSunscreen(p.foodlaCategory, r) },

  // CE marking for toys/electronics/goods/jewelry/medical
  {
    fields: ['ceMarking'],
    condition: (p, r) =>
      !!isGoods(p.foodlaCategory, r) ||
      !!isJewelry(p.foodlaCategory, r) ||
      !!isToy(p.foodlaCategory, r) ||
      !!isElectric(p.foodlaCategory, r) ||
      !!isMedical(p.foodlaCategory, r),
  },

  // Color/material/dimensions for goods (excluding white snus)
  {
    fields: ['color'],
    condition: (p, r) =>
      !isWhiteSnus(p.foodlaCategory, r) &&
      (!!isGoods(p.foodlaCategory, r) ||
        !!isJewelry(p.foodlaCategory, r) ||
        !!isToy(p.foodlaCategory, r) ||
        !!isElectric(p.foodlaCategory, r)),
  },
  {
    fields: ['material', 'dimensions', 'capacity'],
    condition: (p, r) =>
      !isWhiteSnus(p.foodlaCategory, r) &&
      (!!isGoods(p.foodlaCategory, r) ||
        !!isJewelry(p.foodlaCategory, r) ||
        !!isToy(p.foodlaCategory, r) ||
        !!isElectric(p.foodlaCategory, r) ||
        !!isMedical(p.foodlaCategory, r)),
  },

  // Food supplements specific - categoryWeightloss only for isSpecialFoodNutrition per UI
  {
    fields: ['categoryWeightloss'],
    condition: (p, r) => !!isSpecialFoodNutrition(p.foodlaCategory, r),
  },

  // Supplement composition fields
  {
    fields: ['other_substances'],
    condition: (p, r) => !!isSpecialFoodNutrition(p.foodlaCategory, r) || !!isFoodSupplements(p.foodlaCategory, r),
  },
  {
    fields: ['dagsdosAmount', 'dagsdosUnit'],
    condition: (p, r) =>
      shouldShowDailyDose(p, !!isSpecialFoodNutrition(p.foodlaCategory, r) || !!isFoodSupplements(p.foodlaCategory, r)),
  },

  // preparedForm - shown for pharma, detergent, supplements, special nutrition
  {
    fields: ['preparedForm'],
    condition: (p, r) =>
      !!isPharma(p.foodlaCategory, r) ||
      !!isDetergent(p.foodlaCategory, r) ||
      !!isComplementarySupplements(p.foodlaCategory, r) ||
      !!isSpecialFoodNutrition(p.foodlaCategory, r) ||
      !!isFoodSupplements(p.foodlaCategory, r),
  },

  // dosageAndUsageInstructions - also for food categories (special nutrition, supplements)
  {
    fields: ['dosageAndUsageInstructions'],
    condition: (p, r) =>
      (isNewNonFood(p.foodlaCategory, r) ||
        !!isSpecialFoodNutrition(p.foodlaCategory, r) ||
        !!isFoodSupplements(p.foodlaCategory, r)) &&
      !isFeedCategory(p.foodlaCategory, r),
  },

  // country_of_manufacturing - shown for all non-food and many food subcategories
  {
    fields: ['country_of_manufacturing'],
    condition: (p, r) =>
      !!isNewNonFood(p.foodlaCategory, r) ||
      !!isFish(p.foodlaCategory, r, p) ||
      !!isSpecialFoodNutrition(p.foodlaCategory, r) ||
      !!isBeverages(p.foodlaCategory, r) ||
      !!isFoodSupplements(p.foodlaCategory, r) ||
      !!isFruktOrGreen(p.foodlaCategory, r) ||
      !!isOtherFoodstuffs(p.foodlaCategory, r) ||
      !!isMeat(p.foodlaCategory, r) ||
      !!isBakeryProducts(p.foodlaCategory, r) ||
      !!isMilk(p.foodlaCategory, r) ||
      !!isCereals(p.foodlaCategory, r) ||
      !!isConfectionery(p.foodlaCategory, r) ||
      !!isConsumptionReadySnacks(p.foodlaCategory, r) ||
      !!isSweet(p.foodlaCategory, r) ||
      !!isFats(p.foodlaCategory, r) ||
      !!isSpicesSoupsSalads(p.foodlaCategory, r) ||
      !!isIceCream(p.foodlaCategory, r),
  },

  // White snus fields
  {
    fields: [
      'whiteSnusNicotinePerPortion',
      'whiteSnusNicotinePerGram',
      'whiteSnusNicotineStrength',
      'whiteSnusFlavor',
      'whiteSnusPortionsPerUnit',
      'whiteSnusWeightPerPortion',
      'whiteSnusFormat',
    ],
    condition: (p, r) => !!isWhiteSnus(p.foodlaCategory, r),
  },

  // preparationInstructions - only for specific food subcategories
  {
    fields: ['preparationInstructions'],
    condition: (p, r) =>
      !!isFish(p.foodlaCategory, r, p) ||
      !!isSpecialFoodNutrition(p.foodlaCategory, r) ||
      !!isBeverages(p.foodlaCategory, r) ||
      !!isEgg(p.foodlaCategory, r) ||
      !!isFruktOrGreen(p.foodlaCategory, r) ||
      !!isOtherFoodstuffs(p.foodlaCategory, r) ||
      !!isMeat(p.foodlaCategory, r) ||
      !!isBakeryProducts(p.foodlaCategory, r) ||
      !!isMilk(p.foodlaCategory, r) ||
      !!isCereals(p.foodlaCategory, r) ||
      !!isConfectionery(p.foodlaCategory, r) ||
      !!isConsumptionReadySnacks(p.foodlaCategory, r) ||
      !!isSweet(p.foodlaCategory, r) ||
      !!isSpicesSoupsSalads(p.foodlaCategory, r) ||
      !!isFats(p.foodlaCategory, r) ||
      !!isIceCream(p.foodlaCategory, r),
  },

  // non_food_ingredients - only for specific non-food subcategories (NOT all non-food)
  {
    fields: ['non_food_ingredients'],
    condition: (p, r) =>
      !!isToy(p.foodlaCategory, r) ||
      !!isElectric(p.foodlaCategory, r) ||
      !!isJewelry(p.foodlaCategory, r) ||
      !!isGoods(p.foodlaCategory, r) ||
      !!isMedical(p.foodlaCategory, r) ||
      !!isChemical(p.foodlaCategory, r) ||
      !!isDetergent(p.foodlaCategory, r) ||
      !!isCosmetic(p.foodlaCategory, r) ||
      !!isSunscreen(p.foodlaCategory, r) ||
      !!isComplementarySupplements(p.foodlaCategory, r) ||
      !!isOTC(p.foodlaCategory, r),
  },

  // hazardSymbols, hazardStatements, securityNotices - conditional based on category and signalWords
  // These are shown when: (isPharma || isCmrHazardCategory) && !(isChemical || isToy)
  // OR when: (isToy || isChemical) && (signalWords === 'WARNING' || signalWords === 'DANGER')
  {
    fields: ['hazardSymbols', 'hazardStatements', 'securityNotices'],
    condition: shouldShowHazardDetails,
  },
  {
    fields: ['flammableSubtype'],
    condition: (p, r) =>
      shouldShowHazardDetails(p, r) &&
      Array.isArray(p.hazardSymbols) &&
      (p.hazardSymbols as unknown as string[]).includes('FLAMMABLE'),
  },
  {
    fields: ['hazardStatementFills'],
    condition: (p, r) => shouldShowHazardDetails(p, r) && hasPhraseFillControls(p.hazardStatements),
  },
  {
    fields: ['securityNoticeFills'],
    condition: (p, r) => shouldShowHazardDetails(p, r) && hasPhraseFillControls(p.securityNotices),
  },

  // Transport temperature fields - shown when not a store product
  {
    fields: ['transportMinTemperature', 'transportMaxTemperature'],
    condition: (p) => !p.producerIsStore,
  },

  // Gross weight and dimensions - shown when not a Coop store product
  // Must match UI logic: isCoopStoreProduct = producerIsStore && producerOrg?.startsWith('coop/')
  {
    fields: ['gross_weight_num', 'gross_height', 'gross_width', 'gross_depth'],
    condition: (p) => !(p.producerIsStore && p.producerOrg?.startsWith('coop/')),
  },

  // additionalCharacteristics - shown for ALL non-food products (including feed)
  // UI condition: {isNonFood && ( ... )} which includes feed
  {
    fields: ['additionalCharacteristics'],
    condition: (p, r) => !!isNewNonFood(p.foodlaCategory, r),
  },
];

/**
 * Get the list of field names that are visible in the UI for a given product
 */
export function getVisibleFieldsForProduct(product: Partial<IProduct>, rootCategory?: IFoodlaCategoryOption): string[] {
  const visibleFields: string[] = [...ALWAYS_VISIBLE_FIELDS];

  const isFood = isNewFood(product.foodlaCategory, rootCategory);
  const isNonFood = isNewNonFood(product.foodlaCategory, rootCategory);
  const isFeed = isFeedCategory(product.foodlaCategory, rootCategory);

  // Add category-specific base fields
  // Note: Feed categories are under 'Non food' parent, so isNonFood returns true for feed too.
  // We explicitly check !isFeed to ensure feed products get FEED_ONLY_FIELDS, not NON_FOOD_ONLY_FIELDS.
  if (isFood) {
    visibleFields.push(...FOOD_ONLY_FIELDS);
  } else if (isFeed) {
    visibleFields.push(...FEED_ONLY_FIELDS);
  } else if (isNonFood && !isFeed) {
    visibleFields.push(...NON_FOOD_ONLY_FIELDS);
  }

  // Add conditional fields based on specific subcategories
  for (const { fields, condition } of CONDITIONAL_FIELDS) {
    for (const field of fields) {
      if (condition(product, rootCategory) && !visibleFields.includes(field)) {
        visibleFields.push(field);
      }
    }
  }

  return visibleFields;
}

/**
 * Filter product data to only include visible fields for AI review.
 * This creates an object with all fields the user can see in the UI,
 * including empty fields (as empty strings) so the AI can identify
 * which required fields are missing and need to be filled.
 */
export function filterProductForAIReview(
  product: IProduct,
  rootCategory?: IFoodlaCategoryOption
): Record<string, any> {
  const visibleFields = getVisibleFieldsForProduct(product, rootCategory);

  // Always include category name for AI to determine product type
  const filtered: Record<string, any> = {
    category: product.foodlaCategory?.name,
  };

  // Map field names to their actual values
  // Include ALL visible fields so AI can identify missing required fields
  for (const field of visibleFields) {
    if (field === 'foodlaCategory') {
      // Already handled above as 'category'
      continue;
    }

    const value = (product as any)[field];

    // Include all visible fields - convert null/undefined to empty string
    // so the AI knows these fields exist in the UI but are not filled
    if (value === undefined || value === null) {
      filtered[field] = '';
    } else {
      filtered[field] = value;
    }
  }

  filtered.id = product.id

  return filtered;
}

/**
 * Get a human-readable summary of which field categories are visible
 * Useful for logging
 */
export function getVisibleFieldsSummary(
  product: IProduct,
  rootCategory?: IFoodlaCategoryOption
): {
  productType: 'FOOD' | 'FEED' | 'NON_FOOD' | 'UNKNOWN';
  visibleFieldCount: number;
  visibleFields: string[];
} {
  const isFood = isNewFood(product.foodlaCategory, rootCategory);
  const isNonFood = isNewNonFood(product.foodlaCategory, rootCategory);
  const isFeed = isFeedCategory(product.foodlaCategory, rootCategory);

  let productType: 'FOOD' | 'FEED' | 'NON_FOOD' | 'UNKNOWN' = 'UNKNOWN';
  if (isFood) productType = 'FOOD';
  else if (isFeed) productType = 'FEED';
  else if (isNonFood) productType = 'NON_FOOD';

  const visibleFields = getVisibleFieldsForProduct(product, rootCategory);

  return {
    productType,
    visibleFieldCount: visibleFields.length,
    visibleFields,
  };
}
